import React, { useMemo, useState } from 'react'
import classnames from 'classnames'
import KwhCell from '../../components/KwhCell'
import MoneyCell from '../../components/MoneyCell'
import PercentBarCell from '../../components/PercentBarCell'
import type { PropertyTenant } from '../../saber.types'
import { GroupChevron, GroupViewToolbar, GROUP_SUB_ROW_CLASS, SortableTh, nextSort, sortRows } from './grouped'
import type { GroupSort, GroupView, SortValue } from './grouped'
import { displayTenantName } from './labels'
import { columnHelp } from '../../columnHelp'
import { tableBodyRowClass } from '../../tableRowStyles'
import StatusBadge from '../../components/StatusBadge'
import { CELL_COLOR } from '../../cellColor'
import NullDash from '../../components/NullDash'

// §8 — tenant allocation table with a "Group by tenant" toggle: on shows one
// aggregate row per tenant (expandable to tinted meter sub-rows); off shows flat
// per-meter rows. All 8 columns sort per the grouped-sort convention.

const PAD = 'px-3 py-2'

interface TenantNumbers {
  allocationPct: number
  allocatedKwh: number
  grossRevenue: number
  netRevenue: number
  overallocationKwh: number
}

interface TenantGroup extends TenantNumbers {
  tenantCuid: string
  tenantName: string
  meters: PropertyTenant[]
  // WEB-6583: union of the distinct tariff schedule(s) across the tenant's meters.
  tariffSchedules: Set<string>
}

const groupTenants = (rows: PropertyTenant[]): TenantGroup[] => {
  const byTenant = new Map<string, TenantGroup>()
  const out: TenantGroup[] = []
  for (const row of rows) {
    let group = byTenant.get(row.tenantCuid)
    if (group == null) {
      group = {
        tenantCuid: row.tenantCuid,
        tenantName: row.tenantName,
        meters: [],
        allocationPct: 0,
        allocatedKwh: 0,
        grossRevenue: 0,
        netRevenue: 0,
        overallocationKwh: 0,
        tariffSchedules: new Set<string>()
      }
      byTenant.set(row.tenantCuid, group)
      out.push(group)
    }
    group.meters.push(row)
    group.allocationPct += row.allocationPct
    group.allocatedKwh += row.allocatedKwh
    group.grossRevenue += row.grossRevenue
    group.netRevenue += row.netRevenue
    group.overallocationKwh += row.overallocationKwh
    for (const t of row.tariffSchedules ?? []) group.tariffSchedules.add(t)
  }
  return out
}

interface ColumnDef {
  key: string
  label: string
  align?: 'left' | 'right' | 'center'
  groupValue: (g: TenantGroup) => SortValue
  meterValue: (r: PropertyTenant) => SortValue
}

const COLUMNS: ColumnDef[] = [
  { key: 'tenant', label: 'Tenant', groupValue: (g) => g.tenantName, meterValue: (r) => r.tenantName },
  { key: 'meter', label: 'Meter', groupValue: (g) => g.meters.length, meterValue: (r) => r.meterNumber },
  {
    key: 'tariff',
    label: 'Tariff',
    groupValue: (g) => [...g.tariffSchedules].sort().join(', '),
    meterValue: (r) => (r.tariffSchedules ?? []).join(', ')
  },
  { key: 'alloc', label: 'Alloc %', align: 'right', groupValue: (g) => g.allocationPct, meterValue: (r) => r.allocationPct },
  { key: 'allocKwh', label: 'Shadow kWh', align: 'right', groupValue: (g) => g.allocatedKwh, meterValue: (r) => r.allocatedKwh },
  { key: 'gross', label: 'Gross $', align: 'right', groupValue: (g) => g.grossRevenue, meterValue: (r) => r.grossRevenue },
  { key: 'net', label: 'Net $', align: 'right', groupValue: (g) => g.netRevenue, meterValue: (r) => r.netRevenue },
  { key: 'over', label: 'Over-alloc kWh', align: 'right', groupValue: (g) => g.overallocationKwh, meterValue: (r) => r.overallocationKwh }
]

// The five numeric cells shared by rollup, sub- and flat rows.
const numericCells = (x: TenantNumbers, bold = false): React.ReactNode => (
  <>
    <td className={classnames(PAD, 'text-right text-slate-700 dark:text-slate-200', bold && 'font-medium')}>
      <PercentBarCell
        value={x.allocationPct}
        valueClassName={bold ? 'font-medium' : undefined}
        ariaDescription="allocated"
      />
    </td>
    <td className={classnames(PAD, 'text-right text-slate-700 dark:text-slate-200', bold && 'font-medium')}>
      <KwhCell value={x.allocatedKwh} />
    </td>
    <td className={classnames(PAD, 'text-right', bold && 'font-medium')}>
      <MoneyCell value={x.grossRevenue} role="money" />
    </td>
    <td className={classnames(PAD, 'text-right', CELL_COLOR.muted, bold && 'font-medium')}>
      <MoneyCell value={x.netRevenue} role="money" />
    </td>
    <td className={classnames(PAD, 'text-right', CELL_COLOR.muted, bold && 'font-medium')}>
      <KwhCell value={x.overallocationKwh} role="warning" />
    </td>
  </>
)

// WEB-6583: blue Tariff cell — the meter's distinct tariff schedule(s) over the
// window (comma-separated; >1 on a mid-period switch). The group row passes the
// union across the tenant's meters; an em dash when none are present.
const tariffCell = (tariffs: string[]): React.ReactNode => (
  <td className={classnames(PAD, 'whitespace-nowrap', CELL_COLOR.tariff)}>
    {tariffs.length > 0 ? tariffs.map((t) => <StatusBadge status={t} key={t} palette="tariff" />) : <NullDash />}
  </td>
)

export interface TenantAllocTableProps {
  rows: PropertyTenant[]
  /** Selected property name — strips the duplicated suffix from tenant names (WEB-6568). */
  propertyName?: string
}

export const TenantAllocTable: React.FC<TenantAllocTableProps> = ({ rows, propertyName }) => {
  const [view, setView] = useState<GroupView>('tenant')
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set())
  const [sort, setSort] = useState<GroupSort>({ key: null, direction: 'asc' })

  const groups = useMemo(() => groupTenants(rows), [rows])
  const multiKeys = useMemo(() => groups.filter((g) => g.meters.length > 1).map((g) => g.tenantCuid), [groups])
  const allOpen = multiKeys.length > 0 && multiKeys.every((k) => expanded.has(k))

  const toggle = (key: string): void => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }

  const sortCol = COLUMNS.find((c) => c.key === sort.key)
  const sortedGroups = useMemo(() => sortRows(groups, sort, sortCol?.groupValue), [groups, sort, sortCol])
  const sortedFlat = useMemo(() => sortRows(rows, sort, sortCol?.meterValue), [rows, sort, sortCol])

  const totals = useMemo(
    () =>
      groups.reduce<TenantNumbers>(
        (acc, g) => ({
          allocationPct: acc.allocationPct + g.allocationPct,
          allocatedKwh: acc.allocatedKwh + g.allocatedKwh,
          grossRevenue: acc.grossRevenue + g.grossRevenue,
          netRevenue: acc.netRevenue + g.netRevenue,
          overallocationKwh: acc.overallocationKwh + g.overallocationKwh
        }),
        { allocationPct: 0, allocatedKwh: 0, grossRevenue: 0, netRevenue: 0, overallocationKwh: 0 }
      ),
    [groups]
  )

  return (
    <div>
      <GroupViewToolbar
        view={view}
        onViewChange={setView}
        canExpand={multiKeys.length > 0}
        allOpen={allOpen}
        onToggleAll={() => setExpanded(allOpen ? new Set() : new Set(multiKeys))}
        ariaLabel="Tenant table view"
      />
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200 text-sm dark:divide-slate-700">
          <thead>
            <tr>
              {COLUMNS.map((c) => (
                <SortableTh
                  key={c.key}
                  label={c.label}
                  align={c.align}
                  active={sort.key === c.key}
                  direction={sort.direction}
                  onClick={() => setSort((prev) => nextSort(prev, c.key))}
                  title={columnHelp('tenantAlloc', c.key)}
                />
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {rows.length === 0 ? (
              <tr>
                <td colSpan={COLUMNS.length} className="px-3 py-6 text-center text-slate-400 dark:text-slate-500">
                  No tenant allocations.
                </td>
              </tr>
            ) : view === 'flat' ? (
              sortedFlat.map((m, index) => (
                <tr key={`${m.tenantCuid}::${m.meterServiceCuid}`} className={tableBodyRowClass(index)}>
                  <td className={classnames(PAD, 'text-slate-700 dark:text-slate-200')}>
                    {displayTenantName(m.tenantName, propertyName)}
                  </td>
                  <td className={classnames(PAD, 'font-mono text-xs text-slate-600 dark:text-slate-300')}>
                    {m.meterNumber}
                  </td>
                  {tariffCell(m.tariffSchedules ?? [])}
                  {numericCells(m)}
                </tr>
              ))
            ) : (
              (() => {
                let rowIndex = 0
                return sortedGroups.flatMap((g) => {
                const multi = g.meters.length > 1
                const open = expanded.has(g.tenantCuid)
                const groupIndex = rowIndex++
                const out = [
                  <tr
                    key={g.tenantCuid}
                    onClick={multi ? () => toggle(g.tenantCuid) : undefined}
                    className={tableBodyRowClass(groupIndex, multi ? 'cursor-pointer' : undefined)}
                  >
                    <td className={PAD}>
                      <span className="inline-flex items-center gap-1.5">
                        {multi ? <GroupChevron open={open} /> : <span className="inline-block w-3" />}
                        <span className="font-medium text-slate-900 dark:text-slate-100">
                          {displayTenantName(g.tenantName, propertyName)}
                        </span>
                      </span>
                    </td>
                    <td className={classnames(PAD, 'font-mono text-xs text-slate-600 dark:text-slate-300')}>
                      {multi ? (
                        <span className="font-sans text-slate-400 dark:text-slate-500">
                          {open ? '' : `${g.meters.length} meters`}
                        </span>
                      ) : (
                        g.meters[0].meterNumber
                      )}
                    </td>
                    {tariffCell([...g.tariffSchedules].sort())}
                    {numericCells(g, multi)}
                  </tr>
                ]
                if (multi && open) {
                  for (const m of g.meters) {
                    out.push(
                      <tr key={`${g.tenantCuid}::${m.meterServiceCuid}`} className={GROUP_SUB_ROW_CLASS}>
                        <td className={classnames(PAD, 'pl-10')}>
                          <span className="text-xs text-slate-400 dark:text-slate-500">└</span>
                        </td>
                        <td className={classnames(PAD, 'font-mono text-xs text-slate-600 dark:text-slate-300')}>
                          {m.meterNumber}
                        </td>
                        {tariffCell(m.tariffSchedules ?? [])}
                        {numericCells(m)}
                      </tr>
                    )
                  }
                }
                return out
              })
              })()
            )}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-slate-300 font-semibold text-slate-900 dark:border-slate-600 dark:text-slate-100">
                <td className={PAD}>TOTAL</td>
                <td className={PAD} />
                <td className={PAD} />
                {numericCells(totals)}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  )
}

export default TenantAllocTable
